import { useCallback } from "react";
import { debounce } from "throttle-debounce";
import {
  runAssistantFunction,
  submitAssistantToolOutputs,
} from "../../../services/api/instances/assistantsApi/methods";
import type {
  AssistantFunctionRunOutput,
  AssistantFunctionRunPostObjType,
  AssistantFunctionToolOutput,
} from "../../../services/api/instances/assistantsApi/types";
import type { StylistValue } from "../../../services/api/instances/stylistApi/stylistTypes";
import {
  createRun,
  getMessages,
  getRunStatus,
} from "../../../services/api/methods";
import type { RunInfo, ThreadInfo } from "../../../services/api/methodsTypes";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectUserInfo } from "../../Auth/redux/selectors";
import { removeSubstring } from "../helpers";
import {
  createStylistThreadAction,
  postStylistMessageAction,
  updateStylistAction,
} from "../redux/actions";
import {
  selectSelectedAssistant,
  selectSelectedStylist,
  selectStylists,
} from "../redux/selectors";
import {
  setSelectedStylistAction,
  setStylistAction,
  setStylistResultLoading,
} from "../redux/slice";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";

export const useStylistPrompt = () => {
  const dispatch = useAppDispatch();
  const selectedAssistant = useAppSelector(selectSelectedAssistant);
  const selectedStylist = useAppSelector(selectSelectedStylist);
  const stylists = useAppSelector(selectStylists);
  const userInfo = useAppSelector(selectUserInfo);

  const storage = createStorageObjectApi<StylistValue>(stylists);

  const updateStylist = useCallback(
    debounce(1000, (postObj: StorageObject<StylistValue>) => {
      dispatch(updateStylistAction(postObj));
    }),
    [],
  );

  const getPostMessage = () => {
    const postMessage = {
      role: "user",
      content: [
        {
          type: "text",
          text: selectedStylist?.value.stylist_object.prompt as string,
        },
      ],
    } as any;
    return postMessage;
  };

  const checkRunStatus = (run: RunInfo, threadId: string) => {
    const timer = setTimeout(function tick() {
      getRunStatus(threadId, run.id).then((runStatus) => {
        switch (runStatus.status) {
          case "in_progress":
            setTimeout(tick, 1000);
            return;
          case "queued":
            setTimeout(tick, 1000);
            return;
          case "requires_action":
            getRunStatus(run.thread_id, run.id).then((runInfo) => {
              updateRunWithAssistantFunctions(runInfo, runInfo.thread_id);
            });
            return;
          case "completed":
            getMessages(threadId).then((messages) => {
              const systemMessage =
                messages.data[messages.data.length - 1].content[0].text.value;
              const updatedStylist = {
                ...selectedStylist,
                value: {
                  stylist_object: {
                    prompt: selectedStylist?.value.stylist_object
                      .prompt as string,
                    result: removeSubstring(systemMessage, "```markdown"),
                  },
                },
              };
              const updatedStylists = storage.updateById(
                selectedStylist?.id as string,
                {
                  value: {
                    stylist_object: {
                      prompt: selectedStylist?.value.stylist_object
                        .prompt as string,
                      result: removeSubstring(systemMessage, "```markdown"),
                    },
                  },
                },
                true,
              );
              updateStylist(updatedStylist as StorageObject<StylistValue>);
              dispatch(
                setStylistAction(
                  updatedStylists as StorageObject<StylistValue>[],
                ),
              );
              dispatch(
                setSelectedStylistAction(
                  updatedStylist as StorageObject<StylistValue>,
                ),
              );
              clearTimeout(timer);
              dispatch(setStylistResultLoading(false));
            });
            return;
          default:
            return;
        }
      });
    });
  };

  const updateRunWithAssistantFunctions = (run: RunInfo, threadId: string) => {
    const promises: Promise<{
      output: AssistantFunctionRunOutput;
      toolId: string;
    }>[] = [];
    const assistantMessageId = selectedAssistant?.id;
    const messageThreadId = threadId;

    run.required_action.submit_tool_outputs.tool_calls.forEach((tool) => {
      const postObject: AssistantFunctionRunPostObjType = {
        params: JSON.parse(tool.function.arguments),
        credentials: {
          openai_key: null,
          base_url: userInfo?.thread_url || "",
          asst_id: assistantMessageId!,
          thread_id: messageThreadId!,
        },
      };

      promises.push(
        runAssistantFunction(tool.function.name, postObject).then((res) => ({
          output: res,
          toolId: tool.id,
        })),
      );
    });

    Promise.all(promises).then((res) => {
      const payloads: AssistantFunctionToolOutput[] = [];
      res.forEach(({ output, toolId }) => {
        const functionOutput = output.function_output;
        const payload = {
          tool_call_id: toolId,
          output: functionOutput ?? "Не найдено, попробовать позже",
        };
        payloads.push(payload);
      });

      const postObj = { tool_outputs: payloads };
      submitAssistantToolOutputs(messageThreadId!, run.id, postObj).then(
        (response) => {
          checkRunStatus(response, response.thread_id);
        },
      );
    });
  };

  const postStylistMessage = () => {
    const instruction = selectedAssistant?.instructions?.concat(
      selectedStylist?.value.stylist_object.result as string,
    );
    /*     result !== ""
				? assistant?.instructions.replace("{$result}", result)
				: assistant?.instructions; */
    const runObj = {
      assistant_id: selectedAssistant?.id as string,
      instructions: instruction,
    };
    dispatch(createStylistThreadAction())
      .unwrap()
      .then((res: ThreadInfo) => {
        dispatch(postStylistMessageAction(getPostMessage()))
          .unwrap()
          .then(() => {
            createRun(res.id, runObj).then((run) => {
              checkRunStatus(run, run.thread_id);
            });
          });
      })
      .catch((error: any) => {
        console.error(error);
      });
  };

  return { postStylistMessage };
};
